import { computed, type ComputedRef, type Ref } from 'vue'
import Fuse from 'fuse.js'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { preferredLocale, SUPPORT_LOCALES } from '@/plugins/i18n'
import { PRODUCT_CLASSES, type AfpClass } from '@/config/deck/classes'
import { normalize } from '@/lib/normalize'
import topicConfig from '@/config/deck/topics.json'
import { useUserStore } from '@/store/user'
import { useAiStore } from '@/store/ai'
import { searchLocalEvents } from '@/store/getters'
import { EVENT_ID_RE, type EventMatch } from '@/lib/events'
import type { AiSkill, Locale, Topic } from '@/types'

const FUZZY_OPTS = {
  keys: ['label'],
  threshold: 0.3,
  ignoreLocation: true,
  minMatchCharLength: 2
}

const PRODUCT_FUZZY_OPTS = {
  ...FUZZY_OPTS,
  minMatchCharLength: 4,
  includeScore: true
}

export interface ProductMatch {
  class: AfpClass
  query: string
}

export function useProductMatcher(
  search: Ref<string>
): ComputedRef<ProductMatch | null> {
  const { t } = useI18n({ useScope: 'global' })

  // Libellés produits nus (« vidéos ») sans accents : « video » doit trouver les vidéos
  const fuse = computed(
    () =>
      new Fuse(
        PRODUCT_CLASSES.map(cls => ({
          class: cls,
          label: normalize(t(`products.${cls}`))
        })),
        PRODUCT_FUZZY_OPTS
      )
  )

  return computed(() => {
    const tokens = search.value.trim().split(/\s+/).filter(Boolean)
    if (!tokens.length) return null
    let best: {
      score: number
      length: number
      cls: AfpClass
      idx: number
    } | null = null
    tokens.forEach((tk, i) => {
      if (tk.length < 4) return
      for (const hit of fuse.value.search(normalize(tk))) {
        const score = hit.score ?? 1
        const length = hit.item.label.length
        // À score égal (« vidéo » ⊂ « vidéos » et « vidéographies »), le libellé le plus court est le plus proche
        if (
          !best ||
          score < best.score ||
          (score === best.score && length < best.length)
        ) {
          best = { score, length, cls: hit.item.class, idx: i }
        }
      }
    })
    if (!best) return null
    const { cls, idx } = best
    const rest = tokens.filter((_, i) => i !== idx)
    while (rest.length && rest[0].length <= 2) rest.shift()
    while (rest.length && rest[rest.length - 1].length <= 2) rest.pop()
    return { class: cls, query: rest.join(' ') }
  })
}

export function useTopicMatcher(
  search: Ref<string>,
  limit = 2
): ComputedRef<Topic[]> {
  const topics = computed(() => topicConfig[preferredLocale.value])
  const fuse = computed(() => new Fuse(topics.value, FUZZY_OPTS))

  return computed(() => {
    const q = search.value.trim()
    if (q.length < 2) return []
    return fuse.value
      .search(q)
      .slice(0, limit)
      .map(r => r.item)
  })
}

// Détecte quand l'utilisateur tape le nom d'une langue supportée, dans la langue
// de l'interface (ex : « anglais » en UI fr, « English » en UI en) → propose
// d'ouvrir une colonne de tout le contenu dans cette langue.
export function useLanguageMatcher(
  search: Ref<string>
): ComputedRef<Locale | null> {
  const { t } = useI18n({ useScope: 'global' })

  // Noms des langues supportées dans la locale UI courante, lus depuis les
  // fichiers de locale (clés `language-name.*`).
  const names = computed(() =>
    SUPPORT_LOCALES.map(code => ({
      code,
      label: normalize(t(`language-name.${code}`))
    }))
  )

  return computed(() => {
    const q = normalize(search.value.trim())
    // ≥ 3 caractères pour éviter les faux positifs (« es », « en »…).
    if (q.length < 3) return null
    return names.value.find(n => n.label.startsWith(q))?.code ?? null
  })
}

// « carte », « map »… (mots-clés de la langue de l'UI, préfixe accepté) en premier mot → ouvrir la carte,
// le reste de la saisie servant de recherche
export function useMapMatcher(
  search: Ref<string>
): ComputedRef<{ query: string } | null> {
  const { t } = useI18n({ useScope: 'global' })
  const keywords = computed(() =>
    t('omnibox.map-keywords')
      .split(',')
      .map(k => normalize(k.trim()))
  )

  return computed(() => {
    const [first = '', ...rest] = search.value.trim().split(/\s+/)
    const word = normalize(first)
    if (word.length < 3) return null
    return keywords.value.some(k => k.startsWith(word))
      ? { query: rest.join(' ') }
      : null
  })
}

// Un short id de dépêche (afpshortid) est alphanumérique de longueur variable
// (≥ 5). La contrainte lettre+chiffre (plus bas) évite de proposer un document
// pour un simple mot ou un nombre.
const DOCUMENT_ID_RE = /^[A-Za-z0-9]{5,}$/
const HAS_LETTER_RE = /[A-Za-z]/
const HAS_DIGIT_RE = /\d/

export type IdentifierKind = 'document' | 'event'

export interface IdentifierMatch {
  kind: IdentifierKind
  id: string
}

export function useIdentifierMatcher(
  search: Ref<string>
): ComputedRef<IdentifierMatch | null> {
  return computed(() => {
    const t = search.value.trim()
    // Le format événement (3 lettres + 2 chiffres) est le plus spécifique :
    // on le teste d'abord. Tout le reste qui ressemble à un id (alphanumérique,
    // ≥ 5, avec lettre ET chiffre) est traité comme un short id de dépêche.
    if (EVENT_ID_RE.test(t)) return { kind: 'event', id: t.toUpperCase() }
    if (!HAS_LETTER_RE.test(t) || !HAS_DIGIT_RE.test(t)) return null
    if (DOCUMENT_ID_RE.test(t)) return { kind: 'document', id: t.toUpperCase() }
    return null
  })
}

export type EventNameMatch = EventMatch

// Recherche floue sur les noms des événements déjà connus localement (table
// Dexie `events`, alimentée par les dépêches déjà sauvegardées) — pas d'appel
// serveur, contrairement aux autres facettes. Délègue au store (getters.ts),
// point d'entrée partagé avec le tool IA search_events. Ne matche jamais par
// id ici : useIdentifierMatcher détecte déjà le format exact (PMU11) et
// l'enrichit séparément dans Omnibox.vue — matcher aussi par id ici
// afficherait le même événement deux fois dans les suggestions.
export function useEventMatcher(
  search: Ref<string>,
  limit = 3
): ComputedRef<EventNameMatch[]> {
  return computed(() =>
    searchLocalEvents(search.value, {
      preferredLangs: [preferredLocale.value],
      limit
    })
  )
}

export function useSkillMatcher(
  search: Ref<string>,
  freeTextLimit = 2,
  slashLimit = 3
): ComputedRef<AiSkill[]> {
  const aiSkillsStore = useAiStore()
  const { aiEnabled } = storeToRefs(useUserStore())

  // L'omnibox ne propose que les skills généraux : les skills contextuels
  // ne sont accessibles que depuis le menu IA d'un viewer de dépêche.
  const fuse = computed(() => new Fuse(aiSkillsStore.suggestions, FUZZY_OPTS))

  return computed(() => {
    if (!aiEnabled.value) return []
    const q = search.value.trim()
    if (!q) return []
    const skills = aiSkillsStore.suggestions
    if (q.startsWith('/')) {
      // Ne matcher que sur le premier mot après le `/` : une fois l'id du
      // skill complété, ce qui suit un espace est un argument (`$ARGUMENTS`),
      // pas une continuation de l'id à matcher.
      const idPart = q.slice(1).split(/\s+/, 1)[0]
      const id = normalize(idPart)
      if (!id) return skills.slice(0, slashLimit)
      return skills
        .filter(s => normalize(s.id).startsWith(id))
        .slice(0, slashLimit)
    }
    if (q.length < 2) return []
    return fuse.value
      .search(q)
      .slice(0, freeTextLimit)
      .map(r => r.item)
  })
}
